<template>
  <div class="ccma-social-media">
    <div v-if="isSaving" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.Saving') }}</p>
      </div>
    </div>

    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.ChangesSaved') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <h4>{{ t('consultantCorner.SocialMedia') }}</h4>
    <p class="section-description">{{ t('consultantCorner.SocialMediaDescription') }}</p>

    <div class="social-media-links">
      <div class="form-group">
        <label>{{ t('consultantCorner.Facebook') }}</label>
        <input
          v-model="formData.facebook"
          type="text"
          class="social-media-input"
        />
      </div>

      <div class="form-group">
        <label>{{ t('consultantCorner.Instagram') }}</label>
        <input
          v-model="formData.instagram"
          type="text"
          class="social-media-input"
        />
      </div>

      <div class="form-group">
        <label>{{ t('consultantCorner.Pinterest') }}</label>
        <input
          v-model="formData.pinterest"
          type="text"
          class="social-media-input"
        />
      </div>

      <div class="form-group">
        <label>{{ t('consultantCorner.YouTube') }}</label>
        <input
          v-model="formData.youtube"
          type="text"
          class="social-media-input"
        />
      </div>

      <div class="form-group">
        <label>{{ t('consultantCorner.TikTok') }}</label>
        <input
          v-model="formData.tiktok"
          type="text"
          class="social-media-input"
        />
      </div>
    </div>

    <div class="form-actions">
      <KButton @click="saveChanges" class="baseBtn primaryBtn" :disabled="!hasChanges || loading">
        {{ loading ? t('consultantCorner.Saving') : t('consultantCorner.Save') }}
      </KButton>
      <KButton @click="cancelChanges" class="baseBtn secondaryBtn" :disabled="loading">
        {{ t('consultantCorner.Cancel') }}
      </KButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Loader } from '@progress/kendo-vue-indicators'
import { PwsProfileSocialGet, PwsProfileSocialSave } from '../../api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'
import { handleError } from '../../services/ErrorService'
import type { IBusinessDetails, ISocialMediaValues, IPwsProfileSocialViewModel } from '../../types/ConsultantCornerTypes'

const { t } = useI18n()
const logger = useLogger()

const props = defineProps<{
  businessDetails: IBusinessDetails | null
}>()

const emit = defineEmits<{
  updateDetails: [details: Partial<IBusinessDetails>]
}>()

interface IFormData {
  facebook?: string
  instagram?: string
  pinterest?: string
  youtube?: string
  tiktok?: string
}

const emptyFormData: IFormData = {
  facebook: '',
  instagram: '',
  pinterest: '',
  youtube: '',
  tiktok: '',
}

const formData = ref<IFormData>({ ...emptyFormData })

const originalData = ref({ ...formData.value })
const resolvedBeeNumber = ref<number | null>(null)
const loading = ref(false)
const isSaving = ref(false)
const saveSuccess = ref(false)

const mapApiToFormData = (socialData?: IPwsProfileSocialViewModel | null): IFormData => {
  if (!socialData) {
    return { ...emptyFormData }
  }

  return {
    facebook: socialData.facebookId ?? socialData.facebook ?? '',
    instagram: socialData.imHandle ?? '',
    pinterest: socialData.pinterestId ?? socialData.pinterest ?? '',
    youtube: socialData.linkedInId ?? '',
    tiktok: socialData.twitterId ?? '',
  }
}

const mapFormDataToApi = (socialData: IFormData): IPwsProfileSocialViewModel => ({
  facebookId: socialData.facebook?.trim() || undefined,
  imHandle: socialData.instagram?.trim() || undefined,
  pinterestId: socialData.pinterest?.trim() || undefined,
  linkedInId: socialData.youtube?.trim() || undefined,
  twitterId: socialData.tiktok?.trim() || undefined,
})

const applyFormData = (newFormData: IFormData) => {
  formData.value = { ...newFormData }
  originalData.value = { ...newFormData }
}

const hasChanges = computed(() => {
  return JSON.stringify(formData.value) !== JSON.stringify(originalData.value)
})

const getBeeNumber = async (): Promise<number | null> => {
  if (props.businessDetails?.beeNumber) {
    resolvedBeeNumber.value = props.businessDetails.beeNumber
    return resolvedBeeNumber.value
  }

  if (resolvedBeeNumber.value) {
    return resolvedBeeNumber.value
  }

  const authenticatedBeeNumber = await GetBeeNumberAuthenicatedUser()
  if (authenticatedBeeNumber) {
    resolvedBeeNumber.value = authenticatedBeeNumber
  }

  return resolvedBeeNumber.value
}

const loadSocialMediaData = async () => {
  try {
    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    loading.value = true
    const response = await PwsProfileSocialGet({ beeNumber })
    const socialData = response.item ?? response.socialMedia
    applyFormData(mapApiToFormData(socialData))
  } catch (error) {
    logger.error('Error loading social media data', error)
    await handleError(error, t)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void loadSocialMediaData()
})

watch(() => props.businessDetails?.beeNumber, (beeNumber) => {
  if (beeNumber) {
    resolvedBeeNumber.value = beeNumber
    void loadSocialMediaData()
  }
})

const saveChanges = async () => {
  try {
    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    saveSuccess.value = false
    isSaving.value = true
    loading.value = true
    const requestPayload = mapFormDataToApi(formData.value)

    await PwsProfileSocialSave({
      beeNumber,
      ...requestPayload
    })

    const updatedSocialMediaValues: ISocialMediaValues = {
      ...formData.value,
    }

    emit('updateDetails', { socialMedia: updatedSocialMediaValues })
    originalData.value = { ...formData.value }
    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 4000)
    logger.info(t('consultantCorner.ChangesSaved'))
  } catch (error) {
    logger.error('Error saving social media data', error)
    await handleError(error, t)
  } finally {
    isSaving.value = false
    loading.value = false
  }
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}

const cancelChanges = () => {
  formData.value = { ...originalData.value }
  logger.info(t('consultantCorner.ChangesDiscarded'))
}
</script>

<style lang="scss">
.ccma-social-media {
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: $panel-radius;
  padding: $card-padding;
  position: relative;
}

.ccma-social-media .loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
  background-color: var(--color-background);
  border-radius: $panel-radius;
}

.ccma-social-media .loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gap-sm;
}

.ccma-social-media .loading-text {
  @include typography-small;
  margin: $spacing-0;
  color: var(--color-text);
}

.ccma-social-media .save-success-notification {
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: $alert-radius;
  padding: $spacing-3;
  margin-bottom: $spacing-4;
}

.ccma-social-media .success-content {
  display: flex;
  align-items: center;
  gap: $gap-sm;
}

.ccma-social-media .success-icon {
  line-height: 1;
}

.ccma-social-media .success-message p {
  @include typography-small;
  margin: $spacing-0;
  color: var(--color-text);
}

.ccma-social-media .success-close {
  margin-left: auto;
  border: 1px solid var(--color-border);
  border-radius: $button-radius;
  background: var(--color-background-soft);
  color: var(--color-text);
  padding: $spacing-1 $spacing-2;
  cursor: pointer;
}

.ccma-social-media h4 {
  @include typography-h4;
  color: var(--color-heading);
  margin: $spacing-0 0 $spacing-2 $spacing-0;
}

.ccma-social-media .section-description {
  @include typography-small;
  color: var(--color-text);
  margin-bottom: $section-margin-sm;
}

.ccma-social-media .social-media-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $gap-md;
  margin-bottom: $section-margin-sm;
}

.ccma-social-media .form-group {
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: $input-radius;
  padding: $spacing-4;
}

.ccma-social-media .form-group label {
  @include typography-small;
  @include font-weight-semibold;
  margin-bottom: $form-label-margin;
  display: block;
  color: var(--color-text);
}

.ccma-social-media .social-media-input {
  width: 100%;
  padding: $spacing-3;
  border: 1px solid var(--color-border);
  @include typography-small;
  border-radius: $input-radius;
  color: var(--color-text);
  background-color: var(--color-background);
  transition: border-color 0.2s ease;
}

.ccma-social-media input.social-media-input:focus {
  border-color: var(--color-heading);
  outline: none;
  box-shadow: none;
}

.ccma-social-media input.social-media-input:focus-visible {
  outline: none;
  box-shadow: none;
}

.ccma-social-media .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: $gap-sm;
  margin-top: $section-margin-sm;
  padding-top: $spacing-4;
  border-top: 1px solid var(--color-border);
}

@media (max-width: 767px) {
  .ccma-social-media {
    padding: $card-padding-sm;
  }

  .ccma-social-media .social-media-links {
    grid-template-columns: 1fr;
  }

  .ccma-social-media .form-actions {
    flex-direction: column;
    justify-content: flex-start;
  }
}
</style>
